import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchOpenTrades, fetchWallets } from '../../store/slices/tradeSlice';
import { usePrices } from '../../hooks/usePrices';
import { Card } from '../../components/ui/Button';
import { gs } from '../../styles/globalStyles';
import { formatUSD, formatPnL, formatDate, pnlClass } from '../../utils/formatters';
import { COINS } from '../../utils/constants';
import { statusBadge } from '../../components/ui/Button';
import { Ico } from '../../components/ui/Icon';
import TVTicker from '../../components/features/charts/TVTicker';

const PriceTicker: React.FC<{ prices: Record<string, number>; marketData: any }> = ({ prices, marketData }) => (
  <div className="overflow-hidden border border-gray-800/50 rounded-xl bg-gray-900/40 mb-6">
    <div className="ticker-track flex gap-8 py-2.5 px-4 whitespace-nowrap">
      {[...COINS, ...COINS].map((coin, i) => {
        const price = prices[coin.symbol] || 0;
        const change = marketData[coin.symbol]?.change_24h || 0;
        return (
          <div key={i} className="flex items-center gap-2.5 shrink-0">
            <span className="text-gray-400 text-xs font-mono font-semibold">{coin.symbol}</span>
            <span className="text-white text-xs font-mono">${price.toLocaleString()}</span>
            <span className={`text-xs font-mono ${change >= 0 ? 'text-primary' : 'text-red-400'}`}>
              {change >= 0 ? '▲' : '▼'} {Math.abs(change).toFixed(2)}%
            </span>
          </div>
        );
      })}
    </div>
  </div>
);

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const { openTrades, wallets, loading } = useAppSelector(s => s.trade);
  const { prices, marketData } = usePrices();

  useEffect(() => {
    dispatch(fetchOpenTrades());
    dispatch(fetchWallets());
  }, [dispatch]);

  const totalPnL = openTrades.reduce((sum, t) => {
    const price = prices[t.cryptocurrency] || 0;
    return sum + (price > 0 ? (t.unrealised_pnl || 0) : 0);
  }, 0);

  const statCards = [
    {
      label: 'Real Balance',
      value: formatUSD(wallets.real?.balance || 0),
      icon: <Ico name="wallet" />,
      link: '/deposit',
      linkLabel: 'Deposit',
      color: 'text-primary',
    },
    {
      label: 'Demo Balance',
      value: formatUSD(wallets.demo?.balance || 0),
      icon: <Ico name="wallet" />,
      color: 'text-blue-400',
    },
    {
      label: 'Open Trades',
      value: openTrades.length,
      icon: <Ico name="chart" />,
      link: '/trade',
      linkLabel: 'Trade Now',
      color: 'text-yellow-400',
    },
    {
      label: 'Unrealised PnL',
      value: formatPnL(totalPnL),
      icon: <Ico name="flash" />,
      color: totalPnL >= 0 ? 'text-primary' : 'text-red-400',
    },
  ];

  return (
    <div className="p-6">
      {/* Greeting */}
      <div className="mb-6">
        <h2 className="font-display text-2xl font-bold text-white">
          Good day, {user?.first_name} 👋
        </h2>
        <p className="text-gray-500 text-sm mt-1">Here's your trading overview</p>
      </div>

      {/* Price ticker */}
      <TVTicker />

      {/* Admin message */}
      {user?.admin_message && (
        <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl flex gap-3">
          <span className="text-yellow-400 text-lg">📢</span>
          <p className="text-yellow-300 text-sm">{user.admin_message}</p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {statCards.map(card => (
          <Card key={card.label} className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <span className={`${card.color} flex items-center`}>{card.icon}</span>
              {card.link && (
                <Link to={card.link} className="text-xs text-gray-500 hover:text-primary flex items-center gap-1 transition-colors">
                  {card.linkLabel} <Ico name="arrow_right" />
                </Link>
              )}
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase tracking-wider">{card.label}</p>
              <p className={`font-display font-bold text-xl mt-0.5 ${card.color}`}>{card.value}</p>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Open Positions */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-white">Open Positions</h3>
            <Link to="/trade" className="text-primary text-sm hover:underline flex items-center gap-1">
              View All <Ico name="arrow_right" />
            </Link>
          </div>

          {openTrades.length === 0 ? (
            <Card className="flex flex-col items-center justify-center py-12 text-center">
              <Ico name="chart" className="w-8 h-8 text-gray-700 mb-3" />
              <p className="text-gray-500 text-sm">No open positions</p>
              <Link to="/trade" className="mt-3 text-primary text-sm hover:underline">
                Start Trading →
              </Link>
            </Card>
          ) : (
            <div className="flex flex-col gap-3">
              {openTrades.slice(0, 4).map(trade => {
                const price = prices[trade.cryptocurrency] || 0;
                const pnl = price > 0 ? trade.unrealised_pnl || 0 : 0;
                return (
                  <Card key={trade.id} className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xs font-bold ${
                      trade.direction === 'buy' ? 'bg-primary/20 text-primary' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {trade.cryptocurrency}
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-semibold text-sm">{trade.cryptocurrency}/USDT</p>
                      <p className={`text-xs font-semibold ${trade.direction === 'buy' ? 'text-primary' : 'text-red-400'}`}>
                        {trade.direction === 'buy' ? '▲ LONG' : '▼ SHORT'} · {formatUSD(trade.amount)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`font-mono font-bold text-sm ${pnlClass(pnl)}`}>{formatPnL(pnl)}</p>
                      <p className="text-gray-500 text-xs">${parseFloat(String(trade.entry_price)).toLocaleString()}</p>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Market Overview */}
        <div>
          <h3 className="font-display font-semibold text-white mb-4">Markets</h3>
          <Card className="p-0 overflow-hidden">
            {COINS.map((coin: { symbol: string; name: string; color: string }, i: number) => {
              const price = prices[coin.symbol] || 0;
              const change = marketData[coin.symbol]?.change_24h || 0;
              return (
                <div
                  key={coin.symbol}
                  className={`flex items-center justify-between px-4 py-3 ${i !== COINS.length - 1 ? 'border-b border-gray-800/40' : ''} hover:bg-gray-800/30 transition-colors`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-white text-xs font-mono font-bold w-10">{coin.symbol}</span>
                    <span className="text-gray-500 text-xs hidden sm:block">{coin.name}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-white text-xs font-mono">${price.toLocaleString()}</p>
                    <p className={`text-xs font-mono ${change >= 0 ? 'text-primary' : 'text-red-400'}`}>
                      {change >= 0 ? '+' : ''}{change.toFixed(2)}%
                    </p>
                  </div>
                </div>
              );
            })}
          </Card>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
        {[
          { to: '/trade',        icon: <Ico name="chart"      className="w-5 h-5" />, label: 'Trade',   color: 'bg-primary/10 border-primary/30 text-primary'     },
          { to: '/deposit',      icon: <Ico name="arrow_down" className="w-5 h-5" />, label: 'Deposit', color: 'bg-blue-500/10 border-blue-500/30 text-blue-400'   },
          { to: '/withdraw',     icon: <Ico name="arrow_up"   className="w-5 h-5" />, label: 'Withdraw',color: 'bg-orange-500/10 border-orange-500/30 text-orange-400' },
          { to: '/transactions', icon: <Ico name="flash"      className="w-5 h-5" />, label: 'History', color: 'bg-purple-500/10 border-purple-500/30 text-purple-400' },
        ].map(action => (
          <Link
            key={action.to}
            to={action.to}
            className={`flex flex-col items-center justify-center gap-2 py-5 px-3 rounded-xl border transition-all hover:scale-[1.01] ${action.color}`}
          >
            {action.icon}
            <span className="text-xs font-display font-semibold">{action.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default Dashboard;
